import { Button, Label, makeStyles, mergeClasses, Radio, RadioGroup, shorthands, Switch } from '@fluentui/react-components';
import { useId } from '@fluentui/react-hooks';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { customPalette } from '../../../theme';
import { isUndefinedOrEmpty } from '../../../utils/StringUtils';
import { VARIANCE_DISPLAY_FORMAT, VARIANCE_TYPE } from '../../../utils/Variance.constants';
import MaskTextField from '../../common/MaskTextField';

const amountSymbol = '$';
const percentageSymbol = '%';

const useStyles = makeStyles({
    radioGroup: {
        display: 'flex',
        flexDirection: 'row',
        columnGap: '8px',
    },
    radioOption: {
        color: customPalette.blueDark,
        display: 'flex',
        alignItems: 'center',
        height: '46px',
        width: '171px',
        paddingTop: '12px',
        paddingBottom: '12px',
        paddingLeft: '12px',
        paddingRight: '12px',
        marginTop: '0',
        marginRight: '8px',
        marginBottom: '8px',
        marginLeft: '0',
        borderTopWidth: '1px',
        borderRightWidth: '1px',
        borderBottomWidth: '1px',
        borderLeftWidth: '1px',
        borderTopStyle: 'solid',
        borderRightStyle: 'solid',
        borderBottomStyle: 'solid',
        borderLeftStyle: 'solid',
        borderTopColor: customPalette.neutralSecondaryAlt,
        borderRightColor: customPalette.neutralSecondaryAlt,
        borderBottomColor: customPalette.neutralSecondaryAlt,
        borderLeftColor: customPalette.neutralSecondaryAlt,
        borderTopLeftRadius: '8px',
        borderTopRightRadius: '8px',
        borderBottomLeftRadius: '8px',
        borderBottomRightRadius: '8px',
        boxShadow: '0px 5px 4px -4px rgba(0, 0, 0, 0.05), 0px 1px 2px 0px rgba(0, 0, 0, 0.15)',
        backgroundImage: 'linear-gradient(0deg, #FFFFFF, #FFFFFF), linear-gradient(0deg, #E7E8E9, #E7E8E9)',
        fontSize: '14px',
        fontWeight: '600',
        lineHeight: '16px',
        letterSpacing: '0px',
        textAlign: 'left',
        ':hover': {
            color: customPalette.themePrimary,
            borderTopColor: customPalette.themePrimary,
            borderRightColor: customPalette.themePrimary,
            borderBottomColor: customPalette.themePrimary,
            borderLeftColor: customPalette.themePrimary,
        },
    },
    radioOptionSelected: {
        color: customPalette.themePrimary,
        borderTopColor: customPalette.themePrimary,
        borderRightColor: customPalette.themePrimary,
        borderBottomColor: customPalette.themePrimary,
        borderLeftColor: customPalette.themePrimary,
    },
    switchLabel: {
        marginTop: 'unset',
        marginRight: 'unset',
        marginBottom: 'unset',
        marginLeft: 'unset',
    },
    modalContent: {
        display: 'flex',
        flexDirection: 'column',
        maxWidth: '100%',
        width: '400px',
        height: 'auto',
        backgroundImage: 'linear-gradient(0deg, #FFFFFF, #FFFFFF), linear-gradient(0deg, #E7E8E9, #E7E8E9)',
        boxShadow: '0px 25.6px 57.6px 0px rgba(0, 0, 0, 0.22)',
        borderRadius: '12px',
    },
    cardHeader: {
        borderBottom: '1px solid rgba(231, 232, 233, 1)',
        padding: '16px 24px',
    },
    cardHeaderText: {
        fontSize: '18px',
        fontWeight: 600,
        lineHeight: '28px',
        letterSpacing: '-0.256px',
        textAlign: 'left',
        margin: '0',
    },
    setThresholdSection: {
        display: 'flex',
        flexDirection: 'column',
        width: '400px',
        height: 'auto',
        gap: '16px',
        padding: '20px',
    },
    setText: {
        fontSize: '14px',
        fontWeight: 600,
        lineHeight: '16px',
        letterSpacing: '-0.128px',
        textAlign: 'left',
        margin: '0',
    },
    thresholdRow: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: '10px',
    },
    typeButton: {
        width: '56px',
        height: '48px',

        border: '1px solid rgba(231, 232, 233, 1)',
        backgroundImage: 'linear-gradient(0deg, #FFFFFF, #FFFFFF), linear-gradient(0deg, #E7E8E9, #E7E8E9)',
        boxShadow: '0px 5px 4px -4px rgba(0, 0, 0, 0.02), 0px 1px 2px 0px rgba(0, 0, 0, 0.06)',
        cursor: 'pointer',
        minWidth: '56px',
        '& > span': {
            color: customPalette.blueDark,
        },
    },
    typeButtonActive: {
        color: customPalette.themePrimary,
        ...shorthands.borderColor(customPalette.themePrimary),
        //borderColor: customPalette.themePrimary,

        '& > span': {
            fontSize: '14px',
            color: customPalette.themePrimary,
        },
    },
    cardFooter: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'flex-end',
        gap: '10px',
        backgroundColor: 'rgba(251, 251, 252, 1)',
        padding: '16px 20px',
    },
    defaultButton: {},
    primaryButton: {
        backgroundColor: customPalette.themePrimary,

        border: 'none',
        ':hover': {
            border: 'none',
            backgroundColor: customPalette.themePrimary,
        },
    },
});

export interface IVarianceThresholdPanelProps {
    displayVarianceThreshold: any;
    varianceType: any;
    varianceDisplayFormat: any;
    shouldShowOnlyExceedingRows: boolean;
    setVarianceThreshold: (value: any) => void;
    setDisplayVarianceThreshold: (value: any) => void;
    setVarianceType: (value: any) => void;
    setVarianceDisplayFormat: (value: any) => void;
    saveChangesAndCloseDialog: () => void;
    toggleIsDialogVisible: () => void;
    setShouldShowOnlyExceedingRows: (value: boolean) => void;
}

const VarianceThresholdPanel: FC<IVarianceThresholdPanelProps> = ({
    displayVarianceThreshold,
    varianceType = VARIANCE_TYPE.AMOUNT,
    varianceDisplayFormat = VARIANCE_DISPLAY_FORMAT.AMOUNT,
    shouldShowOnlyExceedingRows = false,
    setVarianceThreshold,
    setDisplayVarianceThreshold,
    setVarianceType,
    setVarianceDisplayFormat,
    saveChangesAndCloseDialog,
    toggleIsDialogVisible,
    setShouldShowOnlyExceedingRows,
}) => {
    const { t } = useTranslation();
    const styles = useStyles();
    const textFieldId = useId('variance-threshold-text-input');
    const displayFormatLabelId = useId('variance-display-format-label');

    const onChangeVarianceThresholdHandler = (_event: any, value: string | undefined) => {
        const inputValue = value || '';

        setDisplayVarianceThreshold(inputValue);

        if (!isUndefinedOrEmpty(inputValue)) {
            const valueWithoutCommas = inputValue.replace(/,/g, '');
            const parsedValue = parseFloat(valueWithoutCommas);

            if (!isNaN(parsedValue)) {
                setVarianceThreshold(parsedValue);
            } else {
                setVarianceThreshold('');
            }
        } else {
            setVarianceThreshold('');
        }
    };

    const changeVarianceType = (newType: string) => {
        setVarianceType(newType);
        setVarianceThreshold('');
        setDisplayVarianceThreshold('');
    };

    const onDisplayFormatChange = (_ev: unknown, data: { value: string }) => {
        setVarianceDisplayFormat(data.value);
    };

    const updateShouldShowOnlyExceedingRowsValue = (_: unknown, data: { checked: boolean }) => {
        setShouldShowOnlyExceedingRows(data.checked);
    };

    return (
        <div className={styles.modalContent} role='menu'>
            <div className={styles.cardHeader} role='menuitem'>
                <h3 className={styles.cardHeaderText}>{t('setYourVarianceThreshold')}</h3>
            </div>
            <div className={styles.setThresholdSection} role='menu'>
                <div>
                    <Label htmlFor={textFieldId} className={styles.setText}>
                        {t('setThreshold')}:
                    </Label>
                    <div className={styles.thresholdRow} role='menuitem'>
                        <MaskTextField
                            testid={t('varianceThresholdInput').toString()}
                            id={textFieldId}
                            type='text'
                            value={displayVarianceThreshold}
                            prefix={varianceType === VARIANCE_TYPE.AMOUNT ? amountSymbol : percentageSymbol}
                            onChange={onChangeVarianceThresholdHandler}
                        />
                        <Button
                            appearance='secondary'
                            className={mergeClasses(styles.typeButton, varianceType === VARIANCE_TYPE.AMOUNT && styles.typeButtonActive)}
                            aria-label={t('amountThreshold').toString()}
                            onClick={() => changeVarianceType(VARIANCE_TYPE.AMOUNT)}
                        >
                            {amountSymbol}
                        </Button>
                        <Button
                            appearance='secondary'
                            className={mergeClasses(
                                styles.typeButton,
                                varianceType === VARIANCE_TYPE.PERCENTAGE && styles.typeButtonActive
                            )}
                            aria-label={t('percentageThreshold').toString()}
                            onClick={() => changeVarianceType(VARIANCE_TYPE.PERCENTAGE)}
                        >
                            {percentageSymbol}
                        </Button>
                    </div>
                </div>
                <div>
                    <Label id={displayFormatLabelId} className={styles.setText}>
                        {t('showTableVarianceAs').toString()}
                    </Label>
                    <RadioGroup
                        role='menuitem'
                        layout='horizontal'
                        className={styles.radioGroup}
                        value={varianceDisplayFormat}
                        onChange={onDisplayFormatChange}
                        aria-label={t('showTableVarianceAs').toString()}
                        aria-labelledby={displayFormatLabelId}
                    >
                        <Radio
                            value={VARIANCE_DISPLAY_FORMAT.AMOUNT}
                            label={t('numerical')}
                            className={mergeClasses(
                                styles.radioOption,
                                varianceDisplayFormat === VARIANCE_DISPLAY_FORMAT.AMOUNT && styles.radioOptionSelected
                            )}
                        />
                        <Radio
                            value={VARIANCE_DISPLAY_FORMAT.PERCENTAGE}
                            label={t('percentage')}
                            className={mergeClasses(
                                styles.radioOption,
                                varianceDisplayFormat === VARIANCE_DISPLAY_FORMAT.PERCENTAGE && styles.radioOptionSelected
                            )}
                        />
                    </RadioGroup>
                </div>
                <div>
                    <Switch
                        role='menuitemcheckbox'
                        checked={shouldShowOnlyExceedingRows}
                        label={{
                            children: t('hideRowsNotExceedingThreshold').toString(),
                            className: styles.switchLabel,
                        }}
                        labelPosition='before'
                        onChange={updateShouldShowOnlyExceedingRowsValue}
                    />
                </div>
            </div>
            <div className={styles.cardFooter}>
                <Button
                    appearance='primary'
                    aria-label={t('setThreshold').toString()}
                    className={styles.primaryButton}
                    onClick={saveChangesAndCloseDialog}
                >
                    {t('setThreshold').toString()}
                </Button>
                <Button
                    appearance='secondary'
                    aria-label={t('cancel').toString()}
                    className={styles.defaultButton}
                    onClick={toggleIsDialogVisible}
                >
                    {t('cancel').toString()}
                </Button>
            </div>
        </div>
    );
};

export default VarianceThresholdPanel;
